import { useQuery } from '@tanstack/react-query';
import {
  RSI_DEFAULT_PERIOD,
  type Candle,
  type RsiAnalysis,
  type RsiPoint,
  type Timeframe,
} from '@pipyar/core';
import { api } from '@/lib/api';

export interface RsiResponse {
  symbol: string;
  timeframe: Timeframe;
  period: number;
  digits: number;
  storedGmtOffsetSec: number;
  candles: Candle[];
  points: RsiPoint[];
  analysis: RsiAnalysis;
}

export interface RsiSummaryResponse {
  symbol: string;
  period: number;
  timeframes: RsiAnalysis[];
}

export function useRsi(
  symbol: string | undefined,
  timeframe: Timeframe,
  period = RSI_DEFAULT_PERIOD,
) {
  return useQuery({
    queryKey: ['rsi', symbol, timeframe, period],
    queryFn: () =>
      api.get<RsiResponse>(
        `/api/market/rsi?symbol=${encodeURIComponent(symbol!)}&timeframe=${timeframe}&period=${period}`,
      ),
    enabled: Boolean(symbol),
    staleTime: 20_000,
    refetchInterval: 30_000,
  });
}

export function useRsiSummary(symbol: string | undefined, period = RSI_DEFAULT_PERIOD) {
  return useQuery({
    queryKey: ['rsi-summary', symbol, period],
    queryFn: () =>
      api.get<RsiSummaryResponse>(
        `/api/market/rsi/summary?symbol=${encodeURIComponent(symbol!)}&period=${period}`,
      ),
    enabled: Boolean(symbol),
    // The dashboard polls fast: these summaries are cheap (tens of
    // milliseconds server-side) and a board you glance at should not be showing
    // a price from a minute ago. React Query pauses the timer while the tab is
    // in the background, so this costs nothing when you are not looking.
    staleTime: 4_000,
    refetchInterval: 5_000,
  });
}

/** Net directional read across all timeframes, for the dashboard widget. */
export function rsiTotals(rows: RsiAnalysis[] | undefined) {
  const list = rows ?? [];
  const bullish = list.filter((r) => r.bias === 'bullish').length;
  const bearish = list.filter((r) => r.bias === 'bearish').length;
  const neutral = list.filter((r) => r.bias === 'neutral').length;
  const divergences = list.reduce((n, r) => n + r.divergences.length, 0);
  return { bullish, bearish, neutral, divergences };
}
